import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { methodSystems, osteopathyPrinciples } from '../mockData';
import { Card, CardContent } from './ui/card';
import { Check } from 'lucide-react';
import { createStaggerContainer, fadeInUp, fadeInLeft, getScrollAnimationProps, scaleIn } from '../lib/animations';

const MethodSection = () => {
  const shouldReduceMotion = useReducedMotion();
  const scrollAnimationProps = getScrollAnimationProps(shouldReduceMotion, { amount: 0.25 });
  const containerVariants = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.18, delayChildren: 0.1 });

  return (
    <motion.section id="methode" className="py-12 sm:py-16 lg:py-20 bg-[#F5F0E8]" {...scrollAnimationProps} variants={containerVariants}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <motion.div className="text-center mb-8 sm:mb-12 lg:mb-16" variants={createStaggerContainer({ staggerChildren: 0.12 })}>
          <motion.h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#5A5045] mb-3 sm:mb-4" variants={fadeInUp}>
            Ma Méthode
          </motion.h2>
          <motion.p className="text-sm sm:text-base lg:text-lg text-[#6B5E54] max-w-3xl mx-auto px-4" variants={fadeInUp}>
            Ma pratique se concentre sur une approche globale du corps, en considérant les interactions et synergies entre différents systèmes pour optimiser votre santé et votre bien-être.
          </motion.p>
        </motion.div>

        {/* Systems Grid */}
        <motion.div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16" variants={containerVariants}>
          {methodSystems.map((system) => (
            <motion.div
              key={system.id}
              variants={scaleIn}
              whileHover={shouldReduceMotion ? undefined : { y: -6 }}
            >
              <Card className="group border-none shadow-lg bg-white rounded-sm overflow-hidden">
                <CardContent className="p-0">
                  <div className="relative overflow-hidden h-40 sm:h-48">
                    <motion.img
                      src={system.image}
                      alt={system.name}
                      className="w-full h-full object-cover"
                      whileHover={shouldReduceMotion ? undefined : { scale: 1.08 }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    />
                    <div className="absolute inset-0 bg-[#7D8471]/0 group-hover:bg-[#7D8471]/20 transition-all duration-500"></div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-serif text-[#5A5045] mb-2 sm:mb-3 group-hover:text-[#7D8471] transition-colors duration-300">
                      {system.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-[#6B5E54] leading-relaxed">{system.description}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Osteopathy Info */}
        <motion.div className="bg-white/60 p-6 sm:p-8 lg:p-10 rounded-sm border-l-4 border-[#7D8471]" variants={fadeInUp}>
          <motion.h3 className="text-2xl sm:text-3xl font-serif text-[#5A5045] mb-3 sm:mb-4" variants={fadeInUp}>
            Comprendre l'ostéopathie neuro-endocrinienne
          </motion.h3>
          <motion.p className="text-sm sm:text-base text-[#6B5E54] mb-4 sm:mb-6 leading-relaxed" variants={fadeInUp}>
            L'ostéopathie neuro-endocrinienne est une approche thérapeutique qui considère les interconnexions complexes entre le système nerveux, le système hormonal et la structure physique du corps.
          </motion.p>
          <motion.p className="text-sm sm:text-base text-[#6B5E54] mb-6 sm:mb-8 leading-relaxed" variants={fadeInUp}>
            Cette méthode reconnaît que les déséquilibres dans un système peuvent affecter les autres, créant ainsi des cascades de symptômes qui peuvent sembler sans rapport. En rétablissant l'harmonie entre ces systèmes, le corps retrouve sa capacité naturelle à s'autoréguler et à guérir.
          </motion.p>

          <motion.div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8" variants={createStaggerContainer({ staggerChildren: 0.1 })}>
            {osteopathyPrinciples.map((principle, index) => (
              <motion.div key={index} className="flex items-start space-x-2 sm:space-x-3" variants={fadeInLeft}>
                <Check className="text-[#7D8471] flex-shrink-0 mt-0.5" size={18} />
                <span className="text-xs sm:text-sm text-[#5A5045]">{principle}</span>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="border-t border-[#7D8471]/20 pt-4 sm:pt-6" variants={fadeInUp}>
            <h4 className="text-lg sm:text-xl font-serif text-[#5A5045] mb-2 sm:mb-3">Philosophie de soins</h4>
            <p className="text-xs sm:text-sm text-[#6B5E54] leading-relaxed">
              Je considère chaque patient comme unique, avec son histoire et ses besoins spécifiques. Mon approche vise à identifier la cause profonde des déséquilibres plutôt que simplement traiter les symptômes.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default MethodSection;